import { TaskDetailsTabId } from "@jugl-web/domain-resources/tasks";
import { TasksSource } from "@jugl-web/rest-api/tasks";
import { useTasksSource } from "@web-src/hooks/useTasksSource";
import {
  NavigateFnConfig,
  useNavigation,
} from "@web-src/modules/navigation/hooks/useNavigation";
import { useCallback } from "react";

export interface NavigateToTaskDetailsPageOptions {
  taskId: string;
  /** After navigating to the task details page, the initial tab will be selected. */
  initialTabId?: TaskDetailsTabId;
  /**
   * If provided, the task will be opened in the custom tasks source context.
   * For example, it can be used if we wanted to open the task in the context of its board,
   * instead of the context that we were during the navigation.
   */
  customTasksSource?: TasksSource;
}

export const useNavigateToTaskDetailsPage = () => {
  const { tasksSource } = useTasksSource();

  const { navigateToPage } = useNavigation();

  const navigateToTaskDetailsPage = useCallback(
    (options: NavigateToTaskDetailsPageOptions) => {
      const { taskId, initialTabId, customTasksSource } = options;

      const config: NavigateFnConfig<"taskDetails"> = {
        queryParams: initialTabId ? { tab: initialTabId } : undefined,
        state: {
          source: customTasksSource || tasksSource,
          timestamp: Date.now(),
        },
      };

      if (tasksSource.type === "customerTasks") {
        navigateToPage(
          "customersTaskDetails",
          { customerId: tasksSource.customerId, taskId },
          config
        );

        return;
      }

      navigateToPage("taskDetails", { taskId }, config);
    },
    [navigateToPage, tasksSource]
  );

  return navigateToTaskDetailsPage;
};
